import React from 'react'

import { Chip, ListItem, TextField } from '@material-ui/core'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import ClearIcon from '@material-ui/icons/Clear'
import { Autocomplete } from '@material-ui/lab'

import { useMobile } from '~/hooks/responsive'
import useOptions from '~/hooks/useOptions'
import { isOfType } from '~/utils/functions'

import { Theme } from '~/theme/theme'

import CheckboxSkeleton from '../Skeletons/CheckboxSkeleton'
import Text from '../Text'

import ListboxComponent from './ListboxComponent'
import ListBoxItem from './ListBoxItem'
import { ListBoxProps } from './types'

type OptionSelectedMake = {
  firstLetter: string
  id: string
  label: string
  value: string
}

export interface RenderOptionProps {
  selected: boolean
  option: OptionSelectedMake
  onchange: (value: any, reason: string) => void
  value: string
}

const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles({
      root: {
        '& *::-webkit-scrollbar': {
          width: '4px',
          backgroundColor: '#E9ECEE',
          borderRadius: 4
        },
        '& *::-webkit-scrollbar-thumb': {
          backgroundColor: '#C5CED5',
          borderRadius: 4
        },
        '& .MuiAutocomplete-root': {
          padding: '0 16px',
          '&.d-none': {
            display: 'none'
          }
        },
        '& .MuiAutocomplete-popper': {
          position: 'relative !important',
          width: '100% !important',
          zIndex: 1,
          marginTop: 5
        },
        '& .MuiAutocomplete-option, & .MuiAutocomplete-loading': {
          padding: '0 !important'
        },
        '& .MuiPaper-root': {
          boxShadow: 'none'
        },
        [theme.breakpoints.down('sm')]: {
          '& .MuiAutocomplete-option': {
            display: 'flex',
            flexDirection: 'column',
            '& .MuiCollapse-container': {
              width: '100%'
            }
          },
          '& .MuiAutocomplete-option[aria-selected="true"]': {
            backgroundColor: 'transparent'
          },
          '& .MuiListItem-root.Mui-selected': {
            backgroundColor: 'transparent'
          }
        }
      },
      popperDisablePortal: {
        position: 'relative'
      },
      chip: {
        color: `${theme.palette.text.primary} !important`,
        maxWidth: '100%'
      }
    }),
  { name: 'ListBox' }
)

const placeholderItems = new Array(8).fill(null)

const ListBox = ({
  optionSettings,
  placeholder,
  multiple,
  virtualized = true,
  renderOption,
  hideSearch,
  name,
  id,
  value,
  onChange,
  selectedParent,
  className,
  ...rest
}: ListBoxProps) => {
  const classes = useStyles()
  const isMobile = useMobile()
  const { options: opts, loading, currentValue } = useOptions({
    key: optionSettings?.key || name || id,
    value,
    caseSensitive: false,
    ...(optionSettings || {})
  })

  const emptyValue = multiple ? [] : null

  return (
    <div className={`${classes.root} ListBox`}>
      <Autocomplete
        open
        disableCloseOnSelect
        classes={{ popperDisablePortal: classes.popperDisablePortal }}
        selectOnFocus={false}
        className={`${className} ${hideSearch ? 'd-none' : ''}`}
        fullWidth
        disablePortal
        limitTags={2}
        size="small"
        multiple={multiple}
        options={opts}
        loading={loading}
        defaultValue={value || emptyValue}
        value={currentValue || emptyValue}
        onChange={(e, value, reason) => {
          e.preventDefault()
          e.stopPropagation()
          let newValue

          if (multiple) {
            newValue = value?.map((option) => option?.value)
          } else {
            newValue = value?.value || value
          }

          onChange(newValue, reason)
        }}
        onSelect={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        // inputValue={inputValue}
        // onInputChange={(event, newInputValue, reason) => {
        //   console.log('reason', reason)
        //   setInputValue(newInputValue)
        // }}
        // filterOptions={(x) => x}
        getOptionSelected={(option, values): boolean => {
          return isOfType.boolean(selectedParent)
            ? selectedParent && option.value === values.value
            : option.value === values.value
        }}
        getOptionLabel={(option): string => {
          return option.label
        }}
        ListboxComponent={virtualized ? (ListboxComponent as any) : undefined}
        popupIcon={null}
        renderOption={(option, { selected }) => {
          if (renderOption) {
            return renderOption({
              selected,
              option,
              onChange,
              value: currentValue || emptyValue
            })
          }

          return (
            <ListBoxItem
              selected={selected}
              option={option}
              onChange={onChange}
              value={currentValue || emptyValue}
            />
          )
        }}
        renderTags={(value, getTagProps) => {
          if (isMobile) return false
          return (
            selectedParent &&
            value?.map((option, index) => {
              return (
                <Chip
                  key={option.value}
                  label={option.label}
                  size="small"
                  color="primary"
                  variant="outlined"
                  classes={{ label: classes.chip }}
                  deleteIcon={<ClearIcon color="inherit" />}
                  {...getTagProps({ index })}
                />
              )
            })
          )
        }}
        renderInput={(params) => (
          <TextField
            size="small"
            {...params}
            variant="outlined"
            placeholder={placeholder || 'Search'}
          />
        )}
        loadingText={
          <>
            {placeholderItems.map((_v, index) => (
              <ListItem key={index} style={{ height: 40 }} dense>
                <CheckboxSkeleton alignItems="center" />
              </ListItem>
            ))}
          </>
        }
        noOptionsText={
          <Text textAlign="center" style={{ width: '100%' }}>
            No options
          </Text>
        }
        {...rest}
      />
    </div>
  )
}

export default ListBox
